import { useState } from "react";
import { Button, Divider, Input, Tag, Tooltip } from "antd";
import { CategoryTagEditor } from "components/CategoryTagEditor/CategoryTagEditor";
import {
  ArrowLeft,
  Eye,
  Target,
  Mountain,
  Layers,
  Calendar,
  CalendarDays,
  CalendarClock,
  CheckCircle2,
  AlertTriangle,
  Info,
  Plus,
  Trash2,
  Save,
  DollarSign,
  TrendingUp,
  Users,
  Building2,
  CircleDashed,
} from "lucide-react";
import { motion } from "framer-motion";
import type { AllBuildingBlocks, BuildingBlock, PeakAmbition, ObjectivesBlock, WMBT, Gap } from "../types";
import { normalizeWmbtItems } from "../types";
import { BUILDING_BLOCK_LABELS } from "../schema";
import { getBlockDateLabel } from "../timeframe";
import { ObjectivesBBTab, OBJECTIVES_BB_CONFIGS } from "./ObjectivesBBTab";
import { ProseBBTab, PROSE_BB_CONFIGS } from "./ProseBBTab";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

const blockIcons: Record<string, React.ElementType> = {
  vision: Eye,
  mission: Target,
  peakAmbition: Mountain,
  peakAmbitionObjectives: Layers,
  threeYearObjectives: CalendarDays,
  oneYearObjectives: Calendar,
  quarterlyObjectives: CalendarClock,
};

const blockColors: Record<string, { bg: string; text: string; border: string; strip: string }> = {
  vision:                { bg: "#fff8f4", text: "#f37037", border: "#fdd5be", strip: "#f37037" },
  mission:               { bg: "#f5f0ff", text: "#2c1561", border: "#d4c8f7", strip: "#2c1561" },
  peakAmbition:          { bg: "#fdf4ff", text: "#802a88", border: "#e9c8f0", strip: "#802a88" },
  peakAmbitionObjectives:{ bg: "#f8f0ff", text: "#4b1d70", border: "#dcc8f5", strip: "#4b1d70" },
  threeYearObjectives:   { bg: "#fff0f6", text: "#9b1561", border: "#f5c8df", strip: "#9b1561" },
  oneYearObjectives:     { bg: "#fff0f5", text: "#ef4269", border: "#fdc8d5", strip: "#ef4269" },
  quarterlyObjectives:   { bg: "#f0f6ff", text: "#2c1561", border: "#c8d8f7", strip: "#2c1561" },
};

// ── Dynamic Category Styles ─────────────────────────────────────────────────
// Legacy categories get their original colors; AI-generated labels get a
// deterministic palette rotation so every label always maps to the same color.

export interface CategoryStyle {
  bg: string;
  headerBg: string;
  border: string;
  text: string;
  icon: React.ElementType;
  addColor: string;
}

const LEGACY_CATEGORY_STYLES: Record<string, CategoryStyle> = {
  financial: {
    bg: "#f0fdf4", headerBg: "#dcfce7", border: "#bbf7d0",
    text: "#166534", icon: DollarSign, addColor: "#16a34a",
  },
  market_share: {
    bg: "#eff6ff", headerBg: "#dbeafe", border: "#bfdbfe",
    text: "#1e40af", icon: TrendingUp, addColor: "#2563eb",
  },
  customer: {
    bg: "#f5f3ff", headerBg: "#ede9fe", border: "#ddd6fe",
    text: "#5b21b6", icon: Users, addColor: "#7c3aed",
  },
  custom: {
    bg: "#f8fafc", headerBg: "#f1f5f9", border: "#e2e8f0",
    text: "#334155", icon: Building2, addColor: "#475569",
  },
  // Sentinel for objectives with no assigned category — a neutral zinc chip,
  // deliberately distinct from `custom` ("Company-Specific") so genuinely
  // uncategorized objectives read as such instead of the meaningless "Other".
  uncategorized: {
    bg: "#fafafa", headerBg: "#f4f4f5", border: "#e4e4e7",
    text: "#71717a", icon: CircleDashed, addColor: "#a1a1aa",
  },
};

// Palette for dynamically-generated category labels (8 distinct hues)
const DYNAMIC_PALETTE: Omit<CategoryStyle, "icon">[] = [
  { bg: "#fef2f2", headerBg: "#fee2e2", border: "#fecaca", text: "#991b1b", addColor: "#dc2626" },   // red
  { bg: "#fff7ed", headerBg: "#ffedd5", border: "#fed7aa", text: "#9a3412", addColor: "#ea580c" },   // orange
  { bg: "#fefce8", headerBg: "#fef9c3", border: "#fef08a", text: "#854d0e", addColor: "#ca8a04" },   // yellow
  { bg: "#f0fdf4", headerBg: "#dcfce7", border: "#bbf7d0", text: "#166534", addColor: "#16a34a" },   // green
  { bg: "#ecfeff", headerBg: "#cffafe", border: "#a5f3fc", text: "#155e75", addColor: "#0891b2" },   // cyan
  { bg: "#eff6ff", headerBg: "#dbeafe", border: "#bfdbfe", text: "#1e40af", addColor: "#2563eb" },   // blue
  { bg: "#faf5ff", headerBg: "#f3e8ff", border: "#e9d5ff", text: "#6b21a8", addColor: "#9333ea" },   // purple
  { bg: "#fdf2f8", headerBg: "#fce7f3", border: "#fbcfe8", text: "#9d174d", addColor: "#db2777" },   // pink
];

const DYNAMIC_ICONS: React.ElementType[] = [Target, TrendingUp, Users, Layers, DollarSign, Building2, Eye, Mountain];

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/** Resolve a category style — legacy keys get original colors, everything else gets a deterministic palette slot. */
export function getCategoryStyle(category: string): CategoryStyle {
  if (!category) return LEGACY_CATEGORY_STYLES.uncategorized;
  if (LEGACY_CATEGORY_STYLES[category]) return LEGACY_CATEGORY_STYLES[category];
  const h = hashString(category.toLowerCase());
  const palette = DYNAMIC_PALETTE[h % DYNAMIC_PALETTE.length];
  const icon = DYNAMIC_ICONS[h % DYNAMIC_ICONS.length];
  return { ...palette, icon };
}

// Back-compat alias (referenced by RoadmapGrid, ProgressReflectionPanel, QuarterlyObjectivesTab)
export const CATEGORY_STYLES: Record<string, CategoryStyle> = new Proxy(LEGACY_CATEGORY_STYLES, {
  get: (_target, prop: string) => getCategoryStyle(prop),
});

// Legacy label map — kept for legacy keys; dynamic labels are their own display name
const LEGACY_LABELS: Record<string, string> = {
  financial: "Financial",
  market_share: "Market Share",
  customer: "Customer",
  custom: "Company-Specific",
  uncategorized: "Uncategorized",
};

export function getCategoryLabel(category: string): string {
  if (!category) return "Uncategorized";
  return LEGACY_LABELS[category] ?? category;
}

interface BlockDetailViewProps {
  blockKey: string;
  // Team consensus blocks — always provided.
  buildingBlocks: AllBuildingBlocks;
  // The current viewer's per-user (Solo) blocks. Forwarded to the BB
  // tab so the tab's own Solo/Team toggle can pick the right slice
  // without an extra fetch. Optional because legacy data may not have
  // a viewerOutput row.
  soloBuildingBlocks?: AllBuildingBlocks;
  /** Global Solo/Team mode from the roadmap-level changer. Forwarded to the
   *  child BB tab so focus mode honors the same mode as the grid. */
  viewMode?: "solo" | "team";
  sessionId: string;
  peakDate?: string | null;
  yearStartMonth?: number;
  yearStartDay?: number;
  gaps: Gap[];
  onBack: () => void;
  // Refresh the parent's session state from the server. Replaces the
  // older `onBlocksUpdated(blocks)` partial callback so updates to
  // either viewerOutput or session.buildingBlocks land correctly.
  onSessionRefresh?: () => void;
  isAdvisorOrOwner?: boolean;
  currentUserId?: string;
  /** Viewer's session role; forwarded to ProseBBTab for collab edit/comment gating. */
  userRole?: string;
  onReportCreated?: () => void;
  onLockChange?: () => void;
  autoEdit?: boolean;
  onSendToMainChat?: (message: string) => void;
  /**
   * State 2 gate (2+ participants). Forwarded to the child BB tab so its
   * team-only command-bar controls stay hidden in single-user (State 1)
   * roadmaps where showViewToggle is false.
   */
  showViewToggle?: boolean;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "complete")
    return <Tag icon={<CheckCircle2 className="h-3 w-3" />} color="success">Complete</Tag>;
  if (status === "partial")
    return <Tag icon={<AlertTriangle className="h-3 w-3" />} color="warning">Partial</Tag>;
  return <Tag>Empty</Tag>;
}

// ── WMBT row editor ───────────────────────────────────────────────────────────

function WMBTRow({
  item,
  index,
  catalog,
  onChange,
  onDelete,
  onValidate,
}: {
  item: WMBT;
  index: number;
  catalog: string[];
  onChange: (updated: WMBT) => void;
  onDelete: () => void;
  onValidate?: (updated: WMBT) => void;
}) {
  const style = getCategoryStyle(item.category);

  return (
    <div
      className="rounded-lg border bg-white overflow-hidden group"
      style={{ borderColor: style.border }}
    >
      {/* Item number strip */}
      <div
        className="flex items-center justify-between px-3 py-1.5 border-b"
        style={{ background: style.bg, borderColor: style.border }}
      >
        <span className="text-[10px] font-mono font-bold text-muted-foreground/50 uppercase tracking-widest">
          Item {index + 1}
        </span>
        <div className="flex items-center gap-2">
          <CategoryTagEditor
            value={item.category ? [item.category] : []}
            onChange={(tags) => onChange({ ...item, category: tags[0] || "" })}
            catalog={catalog}
            maxCount={1}
          />
          <Tooltip title="Remove item">
            <button
              className="icon-button-muted opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={onDelete}
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-400 hover:text-red-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="p-3 space-y-2.5">
        {/* Label + Value row */}
        <div className="flex gap-2">
          <div className="flex-[2]">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">What must be true</label>
            <Input
              value={item.label || ""}
              onChange={(e) => onChange({ ...item, label: e.target.value })}
              placeholder="e.g. Achieve product-market fit"
              size="small"
              style={{ fontSize: 13, fontWeight: 500 }}
            />
          </div>
          <div className="flex-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Target value</label>
            <Input
              value={item.value || ""}
              onChange={(e) => onChange({ ...item, value: e.target.value || null })}
              placeholder="e.g. $5M ARR"
              size="small"
              style={{ fontSize: 13 }}
            />
          </div>
        </div>

        {/* Flags row */}
        <div className="flex items-center gap-4 pt-0.5">
          <label
            className="flex items-center gap-1.5 cursor-pointer select-none"
            style={{ color: item.isAssumption ? "#d97706" : "#94a3b8" }}
          >
            <input
              type="checkbox"
              checked={item.isAssumption}
              onChange={(e) => onChange({ ...item, isAssumption: e.target.checked })}
              className="w-3.5 h-3.5 accent-amber-500"
            />
            <span className="text-xs font-medium">Assumption</span>
          </label>

          <label
            className="flex items-center gap-1.5 cursor-pointer select-none"
            style={{ color: item.userValidated ? "#16a34a" : "#94a3b8" }}
          >
            <input
              type="checkbox"
              checked={item.userValidated}
              onChange={(e) => {
                const updated = { ...item, userValidated: e.target.checked };
                onChange(updated);
                onValidate?.(updated);
              }}
              className="w-3.5 h-3.5 accent-emerald-500"
            />
            <span className="text-xs font-medium">Validated</span>
          </label>

          {item.source && (
            <span className="text-[10px] text-muted-foreground/60 italic truncate">
              Source: {item.source}
            </span>
          )}
        </div>

        {/* Assumption rationale */}
        {item.isAssumption && (
          <div
            className="rounded-md p-2.5 border"
            style={{ background: "#fffbeb", borderColor: "#fde68a" }}
          >
            <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1 block">
              Assumption rationale
            </label>
            <Input.TextArea
              value={item.assumptionExplanation || ""}
              onChange={(e) => onChange({ ...item, assumptionExplanation: e.target.value || null })}
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder="Why is this an assumption? What would need to be confirmed?"
              bordered={false}
              style={{ fontSize: 12, color: "#92400e", padding: 0, background: "transparent" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Objectives block editor ───────────────────────────────────────────────────

// Legacy predefined categories — still recognized for backward compat grouping
const LEGACY_CATS = ["financial", "market_share", "customer", "custom"];
// Sentinel grouping bucket for objectives with no assigned category. Without
// this, the category grouping below filtered them out entirely (`.filter(Boolean)`)
// and the objectives silently vanished from the block.
const UNCATEGORIZED = "uncategorized";

function ObjectivesEditor({
  block,
  onSave,
  isSaving,
}: {
  block: ObjectivesBlock;
  onSave: (updated: ObjectivesBlock) => void;
  isSaving: boolean;
}) {
  const [wmbt, setWmbt] = useState<WMBT[]>(normalizeWmbtItems(block.wmbt || []));
  const [customCategories, setCustomCategories] = useState<string[]>(block.customCategories || []);
  const [categoryObjectives, setCategoryObjectives] = useState<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    for (const item of block.wmbt || []) {
      if (item.category && item.categoryObjective) {
        result[item.category] = item.categoryObjective;
      }
    }
    return result;
  });

  // Build the ordered category list from actual WMBT items + any custom categories
  // Legacy predefined cats come first if they have items, then other categories in order of appearance
  const usedCats = Array.from(new Set(wmbt.map(i => i.category || UNCATEGORIZED)));
  const legacyCatsInUse = LEGACY_CATS.filter(c => usedCats.includes(c));
  const nonLegacyCats = usedCats.filter(c => !LEGACY_CATS.includes(c) && !customCategories.includes(c));
  const allCats = [...legacyCatsInUse, ...customCategories.filter(c => !legacyCatsInUse.includes(c)), ...nonLegacyCats];

  const isDirty =
    JSON.stringify(wmbt) !== JSON.stringify(block.wmbt || []) ||
    JSON.stringify(customCategories) !== JSON.stringify(block.customCategories || []) ||
    JSON.stringify(categoryObjectives) !== JSON.stringify(
      Object.fromEntries(
        allCats.map(cat => {
          const existing = (block.wmbt || []).find(i => i.category === cat && i.categoryObjective);
          return [cat, existing?.categoryObjective || ""];
        })
      )
    );

  const updateItem = (index: number, updated: WMBT) => {
    setWmbt(prev => prev.map((item, i) => i === index ? updated : item));
  };

  const deleteItem = (index: number) => {
    setWmbt(prev => prev.filter((_, i) => i !== index));
  };

  const addItem = (category: string) => {
    setWmbt(prev => [...prev, {
      category,
      label: "",
      value: null,
      isAssumption: false,
      assumptionExplanation: null,
      source: null,
      userValidated: false,
      categoryObjective: categoryObjectives[category] || null,
    }]);
  };

  const addCustomCategory = () => {
    const name = `Category ${customCategories.length + 1}`;
    setCustomCategories(prev => [...prev, name]);
  };

  const renameCustomCategory = (idx: number, oldName: string, newName: string) => {
    setCustomCategories(prev => prev.map((c, i) => i === idx ? newName : c));
    setWmbt(prev => prev.map(item => item.category === oldName ? { ...item, category: newName } : item));
    setCategoryObjectives(prev => {
      if (!(oldName in prev)) return prev;
      const next = { ...prev, [newName]: prev[oldName] };
      delete next[oldName];
      return next;
    });
  };

  const removeCustomCategory = (cat: string) => {
    setCustomCategories(prev => prev.filter(c => c !== cat));
    setWmbt(prev => prev.filter(item => item.category !== cat));
  };

  const handleSave = () => {
    const updatedWmbt = wmbt.map(item => ({
      ...item,
      categoryObjective: categoryObjectives[item.category] || item.categoryObjective || null,
    }));
    onSave({ ...block, wmbt: updatedWmbt, customCategories });
  };

  const renderCategorySection = (cat: string, sectionKey: string, isCustom: boolean, customIdx?: number) => {
    const catStyle = getCategoryStyle(cat);
    const CatIcon = catStyle.icon;
    const label = getCategoryLabel(cat);
    const items = wmbt.map((item, i) => ({ item, index: i })).filter(({ item }) => (item.category || UNCATEGORIZED) === cat && item.label != null);

    return (
      <div
        key={sectionKey}
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: catStyle.border }}
      >
        {/* Category header bar */}
        <div
          className="flex items-center justify-between px-4 py-2.5"
          style={{ background: catStyle.headerBg, borderBottom: `1px solid ${catStyle.border}` }}
        >
          <div className="flex items-center gap-2">
            <CatIcon className="h-3.5 w-3.5" style={{ color: catStyle.text }} />
            {isCustom && customIdx !== undefined ? (
              <Input
                value={cat}
                onChange={(e) => renameCustomCategory(customIdx, cat, e.target.value)}
                placeholder="Category name"
                variant="borderless"
                style={{ fontSize: 11, fontWeight: 700, color: catStyle.text, textTransform: "uppercase", letterSpacing: "0.05em", padding: 0, height: "auto", width: 180 }}
              />
            ) : (
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: catStyle.text }}>
                {label}
              </span>
            )}
            {items.length > 0 && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: catStyle.border, color: catStyle.text }}
              >
                {items.length} {items.length === 1 ? "item" : "items"}
              </span>
            )}
          </div>
          {isCustom && customIdx !== undefined && (
            <Tooltip title="Remove this category">
              <button
                className="flex items-center opacity-60 hover:opacity-100 transition-opacity"
                onClick={() => removeCustomCategory(cat)}
              >
                <Trash2 className="h-3.5 w-3.5 text-red-400 hover:text-red-600" />
              </button>
            </Tooltip>
          )}
        </div>

        <div className="p-4 space-y-3" style={{ background: catStyle.bg }}>
          {/* Category objective */}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: catStyle.border }}>
            <div className="px-3 py-1.5 border-b" style={{ background: "rgba(255,255,255,0.7)", borderColor: catStyle.border }}>
              <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: catStyle.text }}>
                Category objective
              </label>
            </div>
            <div className="bg-white">
              <Input
                value={categoryObjectives[cat] || ""}
                onChange={(e) => setCategoryObjectives(prev => ({ ...prev, [cat]: e.target.value }))}
                placeholder={`What's the overall ${label.toLowerCase()} objective?`}
                bordered={false}
                style={{ fontSize: 13, padding: "8px 12px" }}
              />
            </div>
          </div>

          {/* WMBT items */}
          {items.length > 0 && (
            <div className="space-y-2">
              {items.map(({ item, index }, displayIdx) => (
                <WMBTRow
                  key={index}
                  item={item}
                  index={displayIdx}
                  catalog={allCats}
                  onChange={(updated) => updateItem(index, updated)}
                  onDelete={() => deleteItem(index)}
                  onValidate={(updated) => {
                    const newWmbt = wmbt.map((w, i) => i === index ? updated : w);
                    onSave({
                      ...block,
                      wmbt: newWmbt.map(w => ({
                        ...w,
                        categoryObjective: categoryObjectives[w.category] || w.categoryObjective || null,
                      })),
                      customCategories,
                    });
                  }}
                />
              ))}
            </div>
          )}

          {items.length === 0 && (
            <p className="text-xs italic py-1" style={{ color: catStyle.text, opacity: 0.6 }}>
              No items yet — add one below.
            </p>
          )}

          {/* Add item button */}
          <button
            className="flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80 mt-1"
            style={{ color: catStyle.addColor }}
            onClick={() => addItem(cat)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add {label.toLowerCase()} item
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* All categories — unified rendering; custom categories are editable/removable */}
      {allCats.map((cat) => {
        const customIdx = customCategories.indexOf(cat);
        const isCustom = customIdx >= 0;
        return renderCategorySection(cat, `cat-${cat}`, isCustom, isCustom ? customIdx : undefined);
      })}

      {/* Add custom category */}
      <button
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors border border-dashed border-slate-300 rounded-lg px-3 py-2 w-full justify-center"
        onClick={addCustomCategory}
      >
        <Plus className="h-3.5 w-3.5" />
        Add category
      </button>

      <div className="flex justify-end pt-1">
        <Button
          type="primary"
          size="small"
          icon={<Save className="h-3.5 w-3.5" />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!isDirty}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

// ── Main BlockDetailView ──────────────────────────────────────────────────────

export function BlockDetailView({
  blockKey,
  buildingBlocks,
  soloBuildingBlocks,
  viewMode,
  sessionId,
  peakDate,
  yearStartMonth = 1,
  yearStartDay = 1,
  gaps,
  onBack,
  onSessionRefresh,
  isAdvisorOrOwner = false,
  currentUserId,
  userRole,
  onReportCreated,
  onLockChange,
  autoEdit,
  onSendToMainChat,
  showViewToggle = false,
}: BlockDetailViewProps) {
  const [isSaving, setIsSaving] = useState(false);

  const meta = BUILDING_BLOCK_LABELS[blockKey];
  const Icon = blockIcons[blockKey] || Layers;
  const colors = blockColors[blockKey] || blockColors.vision;
  const block = (buildingBlocks as any)[blockKey];
  const dateLabel = getBlockDateLabel(blockKey, peakDate, yearStartMonth, yearStartDay);

  const isPeakAmbition = blockKey === "peakAmbition";
  const isObjectives = blockKey.includes("Objectives") || blockKey === "peakAmbitionObjectives";

  // Compute effective status (same logic as RoadmapGrid) so it reflects real content
  const effectiveStatus = (() => {
    const stored = block.status || "empty";
    if (stored === "complete" || stored === "partial") return stored;
    if (isPeakAmbition && (block.headline || block.draft)) return "partial";
    if (isObjectives && block.wmbt?.length > 0) return "partial";
    if (block.draft) return "partial";
    return "empty";
  })();

  const blockGaps = gaps.filter(g => g.blockKey === blockKey && g.status === "open");

  const saveBlock = async (updatedBlock: BuildingBlock | PeakAmbition | ObjectivesBlock) => {
    setIsSaving(true);
    try {
      const updatedBlocks: AllBuildingBlocks = { ...buildingBlocks, [blockKey]: updatedBlock };
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/blocks`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ buildingBlocks: updatedBlocks }),
        credentials: "include",
      });
      if (res.ok) onSessionRefresh?.();
    } catch (e) {
      console.error("Failed to save block", e);
    } finally {
      setIsSaving(false);
    }
  };

  if (!meta || !block) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.18 }}
      className="h-full flex flex-col overflow-hidden"
    >
      {/* Header — colored left strip */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 border-b bg-white shrink-0"
        style={{ borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: colors.strip }}
      >
        <Button
          type="text"
          size="small"
          icon={<ArrowLeft className="h-3.5 w-3.5" />}
          onClick={onBack}
          style={{ fontSize: 12, color: "#64748b", padding: "0 6px" }}
        >
          All blocks
        </Button>
        <span className="text-slate-300">·</span>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
          style={{ background: colors.bg }}
        >
          <Icon className="h-4 w-4" style={{ color: colors.text }} />
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono text-muted-foreground">{String(meta.number).padStart(2, "0")}</span>
            <span className="text-sm font-semibold">{meta.label}</span>
            {dateLabel && !(blockKey in OBJECTIVES_BB_CONFIGS) && !(blockKey in PROSE_BB_CONFIGS) && (
              <span className="text-[10px] text-primary/70 font-mono">{dateLabel}</span>
            )}
          </div>
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-1">
          {!(blockKey in OBJECTIVES_BB_CONFIGS) && !(blockKey in PROSE_BB_CONFIGS) && (
            <StatusBadge status={effectiveStatus} />
          )}
        </div>
      </div>

      {/* Description strip */}
      <div
        className="px-4 py-2 border-b shrink-0"
        style={{ background: colors.bg, borderColor: colors.border }}
      >
        <p className="text-xs italic leading-snug" style={{ color: colors.text, opacity: 0.8 }}>
          {meta.description}
        </p>
      </div>

      {/* Scrollable content */}
      <div className={`flex-1 min-h-0 bg-slate-50/50 ${(blockKey in OBJECTIVES_BB_CONFIGS || blockKey in PROSE_BB_CONFIGS) ? "overflow-hidden" : "overflow-y-auto p-4 space-y-5"}`}>
        {/* Objectives BB tab — handles its own navigation for all 4 objectives types */}
        {blockKey in OBJECTIVES_BB_CONFIGS ? (
          <ObjectivesBBTab
            config={OBJECTIVES_BB_CONFIGS[blockKey]}
            sessionId={sessionId}
            buildingBlocks={buildingBlocks}
            soloBuildingBlocks={soloBuildingBlocks}
            viewMode={viewMode}
            showViewToggle={showViewToggle}
            isAdvisorOrOwner={isAdvisorOrOwner}
            currentUserId={currentUserId}
            onReportCreated={onReportCreated}
            onSessionRefresh={() => onSessionRefresh?.()}
            onLockChange={onLockChange}
            autoEdit={autoEdit}
            onSendToMainChat={onSendToMainChat}
          />
        ) : blockKey in PROSE_BB_CONFIGS ? (
          <ProseBBTab
            config={PROSE_BB_CONFIGS[blockKey]}
            sessionId={sessionId}
            buildingBlocks={buildingBlocks}
            soloBuildingBlocks={soloBuildingBlocks}
            viewMode={viewMode}
            showViewToggle={showViewToggle}
            isAdvisorOrOwner={isAdvisorOrOwner}
            currentUserId={currentUserId}
            userRole={userRole}
            onReportCreated={onReportCreated}
            onSessionRefresh={() => onSessionRefresh?.()}
            onLockChange={onLockChange}
            autoEdit={autoEdit}
            onSendToMainChat={onSendToMainChat}
          />
        ) : (
          <>
            {isObjectives && (
              <ObjectivesEditor
                block={block}
                onSave={(updated) => saveBlock(updated)}
                isSaving={isSaving}
              />
            )}
          </>
        )}

        {/* Block-scoped gaps — hidden for objectives BB types (managed inside the tab) */}
        {blockGaps.length > 0 && !(blockKey in OBJECTIVES_BB_CONFIGS) && !(blockKey in PROSE_BB_CONFIGS) && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div
              className="rounded-xl border p-4 space-y-2"
              style={{ background: "#fffbeb", borderColor: "#fde68a" }}
            >
              <h3 className="text-xs font-semibold flex items-center gap-1.5 text-amber-800 uppercase tracking-wider">
                <AlertTriangle className="h-3.5 w-3.5" />
                Open gaps for this block
              </h3>
              <div className="space-y-1.5">
                {blockGaps.map(gap => (
                  <div
                    key={gap.id}
                    className="flex items-start gap-2 text-xs text-amber-900 bg-white/70 border border-amber-100 rounded-md px-3 py-2"
                  >
                    <Info className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                    {gap.description}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-amber-700/70 italic">
                Answer in the Gaps tab, or ask Vo directly.
              </p>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
